/* Career Navigation Quiz UI. Ported from docs/reference/quiz.template.html.
   All question text, options, and routes come from quiz.json; never hard-code them here. */
import * as E from "./engine";
import type { Quiz, State, Result, Page, PageLink } from "./types";

const app = document.getElementById("app")!;
const stepEl = document.getElementById("step")!;
const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
const embedded = window.parent !== window;

// Light by default to match the host site. ?theme=dark forces dark; ?theme=auto follows the visitor's system setting.
const theme = new URLSearchParams(location.search).get("theme");
if (theme === "dark" || theme === "auto") document.documentElement.dataset.theme = theme;

let quiz: Quiz;
let state: State;
let qid: string;
// Each entry is the state before a question plus what was picked there, so Back can restore both.
let stack: { state: State; qid: string; picked: string[] }[];
let restore: string[] = [];

const esc = (s: unknown) => String(s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
// Destination pages live on the host site, so leave the iframe when following them.
const link = (url: string, cls: string, label: string) => `<a class="${cls}" href="${esc(url)}" target="_top">${label}</a>`;
// A page has either one url or a list of labelled links; a single url is labelled with the page title.
const destinations = (p: Page): PageLink[] => p.links?.length ? p.links : p.url ? [{ label: p.title, url: p.url }] : [];

function paint(html: string, focus = true) {
  app.innerHTML = `<div class="fade">${html}</div>`;
  const h = app.querySelector<HTMLElement>("h1,h2");
  if (h) {
    h.setAttribute("tabindex", "-1");
    if (focus) h.focus({ preventScroll: true });
  }
  if (!focus) return;
  if (embedded) notify("navigate");
  else window.scrollTo(0, 0);
}

/* Tell the host page our height (for auto-resizing the iframe) and when the screen changes. */
function notify(type: "resize" | "navigate") {
  if (!embedded) return;
  window.parent.postMessage({ source: "dcyf-quiz", type, height: document.documentElement.scrollHeight }, "*");
}

function start(focus = true) {
  stepEl.textContent = "";
  const parts = quiz.title.split(":");
  paint(`<section class="intro"><h1>${esc(parts[1] ? parts[1].trim() : quiz.title)}</h1>
    <p>${esc(quiz.intro)}</p>
    <div class="actions"><button class="btn" id="go">Start the quiz</button></div></section>`, focus);
  document.getElementById("go")!.onclick = () => {
    state = E.initialState();
    stack = [];
    qid = quiz.config.startQuestion;
    ask();
  };
}

function ask() {
  const q = quiz.questions[qid];
  const multi = q.select === "many";
  stepEl.textContent = `Question ${state.history.length + 1}`;
  const prior = restore;
  restore = [];
  const opts = q.options.map(o => `
    <li><button class="opt" data-id="${esc(o.id)}" ${multi ? `role="checkbox" aria-checked="${prior.includes(o.id)}"` : `aria-pressed="${prior.includes(o.id)}"`}>
      <span class="letter" aria-hidden="true">${esc(o.id)}</span>
      <span><span class="t">${esc(o.text)}</span>${o.help ? `<span class="h">${esc(o.help)}</span>` : ""}</span>
    </button></li>`).join("");
  paint(`${quiz.config.showQuestionIds ? `<span class="qid">${esc(qid)}</span>` : ""}
    <h1 id="qtext">${esc(q.text)}</h1>
    <p class="hint">${multi ? "Choose all that apply." : "Choose one."}</p>
    <ul class="options ${multi ? "multi" : ""}" aria-labelledby="qtext">${opts}</ul>
    <div class="actions">
      ${stack.length ? `<button class="link" id="back">Back</button>` : `<span></span>`}
      ${multi ? `<button class="btn" id="next" ${prior.length ? "" : "disabled"}>Continue</button>` : ""}
    </div>`);
  if (stack.length) document.getElementById("back")!.onclick = back;
  const buttons = [...app.querySelectorAll<HTMLButtonElement>(".opt")];
  const optionFor = (b: HTMLElement) => q.options.find(x => x.id === b.dataset.id)!;
  if (multi) {
    const next = document.getElementById("next") as HTMLButtonElement;
    buttons.forEach(b => b.onclick = () => {
      const o = optionFor(b);
      const on = b.getAttribute("aria-checked") !== "true";
      // Choosing an exclusive option clears the others; choosing a plain one clears any exclusive.
      buttons.forEach(x => {
        if (on && x !== b && (o.exclusive || optionFor(x).exclusive)) x.setAttribute("aria-checked", "false");
      });
      b.setAttribute("aria-checked", String(on));
      next.disabled = !buttons.some(x => x.getAttribute("aria-checked") === "true");
    });
    next.onclick = () => answer(buttons.filter(x => x.getAttribute("aria-checked") === "true").map(x => x.dataset.id!));
  } else {
    buttons.forEach(b => b.onclick = () => {
      buttons.forEach(x => x.setAttribute("aria-pressed", "false"));
      b.setAttribute("aria-pressed", "true");
      buttons.forEach(x => x.disabled = true);
      setTimeout(() => answer([b.dataset.id!]), reduce ? 0 : 180);
    });
  }
}

function answer(ids: string[]) {
  stack.push({ state, qid, picked: ids });
  const r = E.route(quiz, state, qid, ids);
  state = r.state;
  if (r.page) show(E.result(quiz, state, r.page));
  else { qid = r.next!; ask(); }
}

function back() {
  const prev = stack.pop()!;
  state = prev.state;
  qid = prev.qid;
  restore = prev.picked;
  ask();
}

function show(res: Result) {
  const p = quiz.pages[res.primary];
  stepEl.textContent = "Your results";
  const dest = destinations(p);
  const go = dest.length
    ? `<div class="golinks">${dest.map(d => link(d.url, "btn btn-link", `Go to ${esc(d.label)}`)).join("")}</div>`
    : `<span class="pending">Page link will be added</span>`;
  const rel = res.related.map(id => {
    const r = quiz.pages[id];
    if (r.links?.length) {
      const links = r.links.map(l => link(l.url, "", esc(l.label))).join("");
      return `<li><span class="rt">${esc(r.title)}</span><span class="rs">${esc(r.summary)}</span><span class="rl">${links}</span></li>`;
    }
    const title = r.url ? link(r.url, "rt", esc(r.title)) : `<span class="rt">${esc(r.title)}</span>`;
    return `<li>${title}<span class="rs">${esc(r.summary)}</span></li>`;
  }).join("");
  const review = state.history.map(q => {
    const qq = quiz.questions[q];
    const a = state.answers[q].map(id => qq.options.find(o => o.id === id)!.text).join(", ");
    return `<div><dt>${esc(qq.text)}</dt><dd>${esc(a)}</dd></div>`;
  }).join("");
  paint(`<p class="message">${esc(res.message)}</p>
    <section class="signpost" aria-labelledby="primary">
      <p class="kicker">Your next step</p>
      <h2 id="primary">${esc(p.title)}</h2>
      <p>${esc(p.summary)}</p>
      ${go}
    </section>
    <h3>You may also want to explore</h3>
    <ul class="related">${rel}</ul>
    <div class="actions"><button class="link" id="back">Back</button><button class="btn" id="again">Start over</button></div>
    <details><summary>Review your answers</summary><dl>${review}</dl></details>`);
  // paint() focuses the first h1/h2, which on results is the destination heading.
  document.getElementById("back")!.onclick = back;
  document.getElementById("again")!.onclick = () => start();
}

if (embedded && "ResizeObserver" in window) new ResizeObserver(() => notify("resize")).observe(document.documentElement);

fetch("quiz.json")
  .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json() as Promise<Quiz>; })
  .then(q => { quiz = q; start(false); }) // Don't pull focus into the iframe on page load.
  .catch(() => {
    app.innerHTML = `<h1>The quiz could not load</h1><p class="hint">Please refresh the page to try again.</p>`;
  });
